import { inject, Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { DOCUMENT } from '@angular/common';
import { firstValueFrom, map, Observable, tap } from 'rxjs';
import { TokensService } from '../http-services/tokens.service';
import { LoginCommand, TokenModel } from '@ose/models';
import { APP_TOKEN } from '@ose/enums';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private tokensService = inject(TokensService);
  private cookieService = inject(CookieService);
  private router = inject(Router);
  private jwtService = inject(JwtHelperService);
  private doc = inject(DOCUMENT);
  private readonly app = inject(APP_TOKEN);

  get isAuthenticated(): boolean {
    const token = this.cookieService.get(
      `${this.tokensService.tokenPrefix}access-token`
    );

    return token ? !this.jwtService.isTokenExpired(token) : false;
  }

  get isTokenExpired(): boolean {
    const token = this.cookieService.get(
      `${this.tokensService.tokenPrefix}access-token`
    );

    return token ? this.jwtService.isTokenExpired(token) : false;
  }

  get hasRefreshToken(): boolean {
    return !!this.cookieService.get('refresh-token');
  }

  async signIn(
    formGroup: LoginCommand,
    returnUrl?: string,
    internal?: boolean
  ): Promise<void> {
    const loginResult$ = this.tokensService.login(formGroup);

    const res = await firstValueFrom(loginResult$);

    this.setTokenInCookie(res);

    sessionStorage.removeItem('auth_return_url');

    if (returnUrl === 'register') {
      await this.router.navigate(['/dashboard'], {
        state: { showSuccessSignup: true },
      });

      return;
    }

    await this.redirectAfterAuthentication(returnUrl);
  }

  async redirectAfterAuthentication(returnUrl?: string): Promise<void> {
    const targetUrl = returnUrl ?? '/dashboard';

    const safeUrl = this.getSafeReturnUrl(targetUrl);

    if (!safeUrl) {
      await this.router.navigateByUrl('/dashboard');
      return;
    }

    const currentOrigin = this.doc.location.origin;

    if (safeUrl.origin === currentOrigin) {
      const internalUrl = safeUrl.pathname + safeUrl.search + safeUrl.hash;

      await this.router.navigateByUrl(internalUrl);

      return;
    }

    this.doc.location.assign(safeUrl.href);
  }

  private getSafeReturnUrl(targetUrl: string): URL | null {
    try {
      const currentOrigin = this.doc.location.origin;

      const parsedUrl = new URL(targetUrl, currentOrigin);

      /*
       * Internal URL
       *
       * Examples:
       * /dashboard
       * /transactions
       * /transactions?id=10
       */
      if (parsedUrl.origin === currentOrigin) {
        return parsedUrl;
      }

      /*
       * External redirects must always use HTTPS.
       */
      if (parsedUrl.protocol !== 'https:') {
        return null;
      }

      const currentHostname = this.doc.location.hostname.toLowerCase();

      /*
       * localhost / dev environments:
       * External redirects are not allowed automatically.
       */
      if (currentHostname === 'localhost' || currentHostname === '127.0.0.1') {
        return null;
      }

      const currentBaseDomain = this.getBaseDomain(currentHostname);

      const targetHostname = parsedUrl.hostname
        .toLowerCase()
        .replace(/^www\./, '');

      const normalizedBaseDomain = currentBaseDomain.replace(/^www\./, '');

      /*
       * Allow:
       *
       * onesecondexchange.com
       * www.onesecondexchange.com
       * client.onesecondexchange.com
       * support.onesecondexchange.com
       * any trusted subdomain of the same root domain
       */
      const isAllowedDomain =
        targetHostname === normalizedBaseDomain ||
        targetHostname.endsWith(`.${normalizedBaseDomain}`);

      if (!isAllowedDomain) {
        return null;
      }

      return parsedUrl;
    } catch {
      return null;
    }
  }

  private getBaseDomain(hostname: string): string {
    const normalizedHostname = hostname.toLowerCase().replace(/^www\./, '');

    const parts = normalizedHostname.split('.');

    if (parts.length <= 2) {
      return normalizedHostname;
    }

    return parts.slice(-2).join('.');
  }

  logout(): Observable<void> {
    return this.tokensService.logout().pipe(
      tap(() => {
        this.clearCookies();

        setTimeout(() => {
          void this.router.navigate(['auth', 'sign-in']);
        }, 200);
      }),
      map(() => void 0)
    );
  }

  refreshToken() {
    this.tokensService.refreshAccessToken().subscribe({
      next: (res: TokenModel) => this.setTokenInCookie(res),
      error: () => this.clearCookies(),
    });
  }

  clearCookies(): void {
    let domain = this.doc.location.hostname;

    const domainSplit = domain.split('.');

    if (domainSplit.length > 2) {
      domain = domainSplit.slice(-2).join('.');
    }

    if (
      typeof sessionStorage !== 'undefined' &&
      sessionStorage.clear instanceof Function
    ) {
      sessionStorage.clear();

      this.cookieService.delete(
        this.tokensService.tokenPrefix + 'access-token',
        '/',
        domain
      );

      this.cookieService.delete('refresh-token', '/', domain);
    }
  }

  setTokenInCookie(tokens: TokenModel) {
    let domain = this.doc.location.hostname;

    if (domain.includes('onesecondexchange')) {
      const domainSplit = domain.split('.');

      if (domainSplit.length > 2) {
        domain = domainSplit.slice(-2).join('.');
      }
    }

    this.cookieService.delete(
      this.tokensService.tokenPrefix + 'access-token',
      '/',
      domain
    );

    this.cookieService.delete('refresh-token', '/', domain);

    const accessTokenExpiresAt = new Date(
      tokens.access_token_expires_at * 1000
    );

    this.cookieService.set(
      this.tokensService.tokenPrefix + 'access-token',
      tokens.access_token,
      accessTokenExpiresAt,
      '/',
      `.${domain}`
    );

    this.tokensService.buildJWT();

    if (tokens.refresh_token) {
      const refreshTokenExpiresAt = new Date(
        tokens.refresh_token_expires_at * 1000
      );

      this.cookieService.set(
        'refresh-token',
        tokens.refresh_token,
        refreshTokenExpiresAt,
        '/',
        `.${domain}`
      );
    }
  }

  hasScope(scope: string): boolean {
    return (
      this.tokensService.jwtPayload?.scopes?.some((p) => p === scope) || false
    );
  }
}
